/**
 * Single source of truth för giltiga `?sort=...`-värden på produktlistor.
 *
 * Allt UI som läser/skriver sort-paramen och alla server-fetcher som tar
 * emot ett sort-värde ska gå genom helpers härifrån. Då kan vi:
 *
 *   - silently fall back till "default" (Relevans) vid trasig URL,
 *     felstavning, gamla bokmärken eller manuell URL-pillning
 *   - logga ogiltiga inkommande värden i dev så vi ser om någon länk
 *     genererar fel sort
 *   - hålla labels (för chip/dropdown/aria) på ett ställe
 *
 * Tillagda lägen MÅSTE läggas in i PRODUCT_SORTS-tuplen, annars förkastas
 * de av `coerceProductsSort` och försvinner ur URL:en.
 */

export const PRODUCT_SORTS = [
  "default",
  "bestseller",
  "newest",
  "price-asc",
  "price-desc",
  "unit-price-asc",
  "name-asc",
  "name-desc",
] as const;

export type ProductsSort = (typeof PRODUCT_SORTS)[number];

const SORT_SET: ReadonlySet<string> = new Set<string>(PRODUCT_SORTS);

export const PRODUCT_SORT_LABELS: Record<ProductsSort, string> = {
  default: "Relevans",
  bestseller: "Bästsäljare",
  newest: "Nyast",
  "price-asc": "Pris: Lågt till högt",
  "price-desc": "Pris: Högt till lågt",
  "unit-price-asc": "Pris per kg: Lågt till högt",
  "name-asc": "Namn: A-Ö",
  "name-desc": "Namn: Ö-A",
};

/** Type guard för okända inputs (URL/JSON/edge-fn-payloads). */
export function isProductsSort(value: unknown): value is ProductsSort {
  return typeof value === "string" && SORT_SET.has(value);
}

/**
 * Konverterar valfri input till ett giltigt `ProductsSort`. Allt som inte
 * matchar normaliseras till "default" — aldrig en throw, aldrig undefined.
 *
 * Trimmar whitespace och case-folding så att `?sort= Price-Asc ` accepteras.
 * Tomma värden (null/undefined/"" / "default") returnerar "default" tyst.
 *
 * I dev loggas ogiltiga icke-tomma värden via console.warn så att trasiga
 * länkar i kod/marknadsföring upptäcks tidigt — i prod är fallbacken tyst.
 */
export function coerceProductsSort(raw: unknown): ProductsSort {
  if (raw == null) return "default";
  if (typeof raw !== "string") {
    warnInvalid(raw);
    return "default";
  }
  const trimmed = raw.trim().toLowerCase();
  if (trimmed === "" || trimmed === "default") return "default";
  // Public URL-alias: `?sort=relevance` är den delbara, läsbara formen för
  // standardsorteringen ("Relevans"). Internt heter den fortfarande "default"
  // så att kategori-sidor (utan sort-param) och söksidor med
  // `?sort=relevance` ger identiskt resultat och back/forward fungerar.
  if (trimmed === "relevance") return "default";
  if (SORT_SET.has(trimmed)) return trimmed as ProductsSort;
  warnInvalid(raw);
  return "default";
}

function warnInvalid(raw: unknown): void {
  // Endast i dev — prod-fallbacken ska vara tyst för att inte spamma
  // sentry/console när bottar pillar i URL:en.
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.warn("[productSort] ignoring invalid ?sort value", raw);
  }
}

/**
 * Minimal kontrakt för en Supabase PostgREST-query som stödjer `.order()`-kedjor.
 * Vi typar det här strukturellt istället för att importera SupabaseClient så att
 * helpern kan användas både från app-kod och från integrationstester utan
 * tunga generics.
 */
export interface OrderableQuery<Q> {
  order(
    column: string,
    options?: { ascending?: boolean; nullsFirst?: boolean },
  ): Q;
}

/**
 * Single source of truth för ORDER BY-kedjan på produktlistor.
 *
 * Både `fetchProductsPaginated` (app-runtime) och integrationstesterna
 * (`productsPaginationSort.integration.test.ts`) MÅSTE gå genom denna helper
 * — annars kan testerna säga GREEN på en sortering som prod faktiskt inte
 * använder, vilket är exakt det vi vill skydda mot.
 *
 * Avslutar alltid med `shopify_id ASC` som stable tiebreaker så paging aldrig
 * duplicerar/skipper rader under lika sort-nycklar.
 */
/**
 * Bygger en produktlist-URL med ett kanoniskt `?sort=...`-värde.
 *
 * Skall användas av ALLT UI som genererar sort-länkar (breadcrumbs,
 * dropdown-alternativ, "rensa filter"-knappar, marknadsförings-CTA på
 * landningssidor m.m.) så att vi aldrig råkar skriva ett icke-kanoniskt
 * värde i URL:en (t.ex. "bestsellers" i pluralis eller "Price-Asc" med
 * versaler) som sedan tyst faller tillbaka till "default" och får
 * användaren att undra varför sorteringen inte fastnade.
 *
 * Regler:
 *  - Bara värden i `PRODUCT_SORTS` (eller alias som `coerceProductsSort`
 *    förstår) propageras; allt annat normaliseras eller utelämnas.
 *  - "default" utelämnas helt — matchar URL-konventionen i grid-komponenten
 *    så att kanoniska URL:er aldrig bär en redundant `?sort=default`.
 *  - Befintliga query-parametrar i `path` bevaras och `sort` ersätts om den
 *    redan finns där (sista vinner — annars vore det otydligt vilket värde
 *    som gäller).
 */
export function buildSortHref(path: string, sort: unknown): string {
  const canonical = coerceProductsSort(sort);
  const [pathPart, hashPart] = path.split("#");
  const [pathname, existingQs = ""] = pathPart.split("?");
  const params = new URLSearchParams(existingQs);
  if (canonical === "default") {
    params.delete("sort");
  } else {
    params.set("sort", canonical);
  }
  const qs = params.toString();
  return `${pathname}${qs ? `?${qs}` : ""}${hashPart ? `#${hashPart}` : ""}`;
}

export function applyProductsSortOrder<Q extends OrderableQuery<Q>>(
  query: Q,
  sort: ProductsSort,
): Q {
  let q: Q = query;
  switch (sort) {
    case "price-asc":
      q = q
        .order("available_for_sale", { ascending: false })
        .order("price", { ascending: true, nullsFirst: false });
      break;
    case "price-desc":
      q = q
        .order("available_for_sale", { ascending: false })
        .order("price", { ascending: false, nullsFirst: false });
      break;
    case "newest":
      q = q
        .order("available_for_sale", { ascending: false })
        .order("shopify_created_at", { ascending: false, nullsFirst: false });
      break;
    case "name-desc":
      q = q
        .order("available_for_sale", { ascending: false })
        .order("title", { ascending: false });
      break;
    case "name-asc":
      q = q
        .order("available_for_sale", { ascending: false })
        .order("title", { ascending: true });
      break;
    case "bestseller":
      q = q
        .order("available_for_sale", { ascending: false })
        .order("sales_count_90d", { ascending: false, nullsFirst: false })
        .order("relevance_score", { ascending: false, nullsFirst: false });
      break;
    case "unit-price-asc":
      q = q
        .order("available_for_sale", { ascending: false })
        .order("unit_price_per_kg", { ascending: true, nullsFirst: false })
        .order("price", { ascending: true, nullsFirst: false });
      break;
    case "default":
    default:
      // Deterministic relevance: composite score in DB, then recency,
      // sedan stable id (tillagd nedan).
      q = q
        .order("relevance_score", { ascending: false, nullsFirst: false })
        .order("shopify_updated_at", { ascending: false, nullsFirst: false });
      break;
  }
  return q.order("shopify_id", { ascending: true });
}
